
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSession } from "@supabase/auth-helpers-react";

export function useChurchData() {
  const { toast } = useToast();
  const session = useSession();
  const [congregationName, setCongregationName] = useState<string>("Grace Community Church");
  const [logo, setLogo] = useState<string | null>("/lovable-uploads/5c561338-e504-4c6d-810f-f9a17db22224.png");
  const [churchCode, setChurchCode] = useState<string>("001");
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchCongregationInfo = useCallback(async () => {
    if (!session?.user?.id) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      console.log("Fetching congregation info for admin user:", session.user.id);
      
      // Check for URL params to see if we have a specific bracelet code
      const urlParams = new URLSearchParams(window.location.search);
      const braceletCode = urlParams.get('id');
      
      if (braceletCode) {
        let cleanBraceletCode = braceletCode.replace(/^GHG-?/, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        
        console.log("Looking up bracelet code:", cleanBraceletCode);
        
        // Specific mapping for TEST001, TEST002, TEST003 to TEST COMM
        if (['TEST001', 'TEST002', 'TEST003'].includes(cleanBraceletCode)) {
          console.log("Found TEST series bracelet:", cleanBraceletCode);
          
          const { data: churchBranding, error: brandingError } = await supabase
            .from("church_branding")
            .select("*")
            .eq("church_code", "001")
            .maybeSingle();
          
          if (brandingError) throw brandingError;
          
          if (churchBranding) {
            console.log("Setting TEST COMM branding for bracelet:", cleanBraceletCode);
            setCongregationName(churchBranding.church_name);
            setLogo(churchBranding.logo_url);
            setChurchCode(churchBranding.church_code);
            setIsReadOnly(true);
            setIsLoading(false);
            return;
          }
        } else {
          console.log("Bracelet not in TEST series, using default Grace Awakens branding");
        }
      }
      
      // Check if the current user is an admin
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'admin')
        .maybeSingle();
      
      if (rolesError) throw rolesError;
      
      if (userRoles) {
        console.log("User is an admin, checking for congregation association");
        
        // Get this admin's church association
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('congregation_name, full_name')
          .eq('id', session.user.id)
          .maybeSingle();
          
        if (profileError) {
          console.error("Error fetching profile:", profileError);
          throw profileError;
        }
        
        if (profile?.congregation_name) {
          console.log("Admin is associated with congregation:", profile.congregation_name);
          
          // Check if this congregation has branding
          const { data: churchBranding, error: brandingError } = await supabase
            .from("church_branding")
            .select("*")
            .eq("church_name", profile.congregation_name)
            .maybeSingle();
            
          if (brandingError) throw brandingError;
          
          if (churchBranding) {
            console.log("Found church branding for this admin's congregation:", churchBranding);
            setCongregationName(churchBranding.church_name);
            setLogo(churchBranding.logo_url);
            setChurchCode(churchBranding.church_code);
            setIsReadOnly(false);
            setIsLoading(false);
            return;
          } else if (profile.full_name === 'Test User') {
            // Special case for our test user
            console.log("Test user detected, setting TEST COMM branding");
            setCongregationName("TEST COMM");
            setLogo("/lovable-uploads/5c561338-e504-4c6d-810f-f9a17db22224.png");
            setChurchCode("001");
            setIsReadOnly(false);
            setIsLoading(false);
            return;
          }
        }
        
        // Admin without specific congregation - check if they've created one
        const { data: congregations, error: congregationsError } = await supabase
          .from('congregations')
          .select('*')
          .eq('created_by', session.user.id)
          .maybeSingle();
          
        if (congregationsError) throw congregationsError;
        
        if (congregations?.name) {
          // Check if this congregation has branding
          const { data: churchBranding, error: brandingError } = await supabase
            .from("church_branding")
            .select("*")
            .eq("church_name", congregations.name)
            .maybeSingle();
            
          if (brandingError) throw brandingError;
          
          if (churchBranding) {
            setCongregationName(churchBranding.church_name);
            setLogo(churchBranding.logo_url);
            setChurchCode(churchBranding.church_code);
            setIsReadOnly(false);
            setIsLoading(false);
            return;
          } else {
            // Congregation exists but no branding yet
            setCongregationName(congregations.name);
            setChurchCode(generateChurchCode());
            setIsReadOnly(false);
            setIsLoading(false);
            return;
          }
        }
      }
      
      // Default to Grace Awakens branding
      setCongregationName("Grace Awakens");
      setLogo("/lovable-uploads/5c561338-e504-4c6d-810f-f9a17db22224.png");
      setChurchCode("GRACE");
      setIsReadOnly(false);
    } catch (error) {
      console.error("Error fetching congregation info:", error);
      toast({
        title: "Error",
        description: "Failed to load congregation information",
        variant: "destructive",
      });
      
      // Fallback to Grace Awakens
      setCongregationName("Grace Awakens");
      setLogo("/lovable-uploads/5c561338-e504-4c6d-810f-f9a17db22224.png");
      setChurchCode("GRACE");
      setIsReadOnly(false);
    } finally {
      setIsLoading(false);
    }
  }, [session, toast]);
  
  const generateChurchCode = () => {
    // Generate a random 3-digit code
    return Math.floor(100 + Math.random() * 900).toString();
  };

  useEffect(() => {
    if (session?.user?.id) {
      fetchCongregationInfo();
    }
  }, [session?.user?.id, fetchCongregationInfo]);

  const saveChurchData = async () => {
    if (!session?.user?.id) {
      toast({
        title: "Error",
        description: "You must be logged in to save church settings",
        variant: "destructive",
      });
      return false;
    }
    
    try {
      console.log("Saving church data:", { congregationName, logo, churchCode });
      
      // Check if this church code already exists
      const { data: existingBranding, error: checkError } = await supabase
        .from("church_branding")
        .select("*")
        .eq("church_code", churchCode)
        .maybeSingle();
        
      if (checkError) {
        console.error("Error checking existing branding:", checkError);
        throw checkError;
      }
      
      if (existingBranding) {
        // Update existing record
        console.log("Updating existing church branding record with data:", {
          church_name: congregationName,
          logo_url: logo
        });
        
        const { error: updateError } = await supabase
          .from("church_branding")
          .update({
            church_name: congregationName,
            logo_url: logo
          })
          .eq("church_code", churchCode);
          
        if (updateError) {
          console.error("Error updating church branding:", updateError);
          throw updateError;
        }
        
        console.log("Church branding updated successfully");
      } else {
        // Create new record
        console.log("Creating new church branding record with data:", {
          church_code: churchCode,
          church_name: congregationName,
          logo_url: logo
        });
        
        const { error: insertError } = await supabase
          .from("church_branding")
          .insert({
            church_code: churchCode,
            church_name: congregationName,
            logo_url: logo,
            primary_color: "#0F4C81",
            secondary_color: "#1EAEDB"
          });
          
        if (insertError) {
          console.error("Error inserting church branding:", insertError);
          throw insertError;
        }
        
        console.log("New church branding record created successfully");
      }
      
      // Update user's congregation association
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ congregation_name: congregationName })
        .eq("id", session.user.id);
        
      if (profileError) {
        console.warn("Could not update user profile with congregation:", profileError);
      }
      
      // For test user, check if email matches "user@test.com"
      const { data: profile, error: profileFetchError } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', session.user.id)
        .single();
        
      if (profileFetchError) {
        console.warn("Could not fetch user profile:", profileFetchError);
      }
      
      // If this appears to be our test user and they're setting up TEST COMM
      if (profile?.full_name === "Test User" && congregationName === 'TEST COMM') {
        console.log("Ensuring TEST series bracelets are associated with TEST COMM");
        
        const testCodes = ['TEST001', 'TEST002', 'TEST003'];
        
        for (const code of testCodes) {
          const { data: existingBracelet, error: braceletError } = await supabase
            .from("bracelets")
            .select("*")
            .eq("referral_code", code)
            .maybeSingle();
            
          if (braceletError) {
            console.warn(`Error checking bracelet ${code}:`, braceletError);
            continue;
          }
            
          if (existingBracelet) {
            // Update existing bracelet
            await supabase
              .from("bracelets")
              .update({ church_code: churchCode })
              .eq("referral_code", code);
          } else {
            // Create new bracelet record
            await supabase
              .from("bracelets")
              .insert({
                referral_code: code,
                church_code: churchCode,
                created_by_user_id: session.user.id,
                created_at: new Date().toISOString()
              });
          }
        }
      }
      
      // Force reload church branding context by triggering a custom event
      window.dispatchEvent(new CustomEvent('church-branding-updated'));
      
      console.log("Church data saved successfully, event dispatched");
      
      toast({
        title: "Success",
        description: "Church settings saved successfully. Changes will be visible on all pages.",
      });
      
      return true;
    } catch (error: any) {
      console.error("Error saving church data:", error);
      toast({
        title: "Error",
        description: "Failed to save congregation settings: " + error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    congregationName,
    setCongregationName,
    logo,
    setLogo,
    churchCode,
    setChurchCode,
    isReadOnly,
    isLoading,
    fetchCongregationInfo,
    saveChurchData
  };
}
